<template>
  <div class="search-facet" v-if="hasFacet && def">
    <div class="facet-title">
      <h3 class="is-size-7-touch">
        {{ ls(def.name) }}
      </h3>
      <button
        class="jps-icon-button facet-toggle"
        type="button"
        aria-hidden="true"
        @click="showFacet = !showFacet"
      >
        <BaseIcon
          :name="showFacet ? 'mdi/menu-up' : 'mdi/menu-down'"
        />
      </button>
    </div>
    <div class="search-facet-facets" v-show="showFacet">
      <div
        class="search-facet-facet"
        :class="{ 'is-selected': isSelected(value) }"
        v-for="[value, count] in Array.from(sortedFacet)"
        :key="value"
      >
        <BaseCheckbox
          :model-value="isSelected(value)"
          @update:model-value="select(value)"
        >
          <SearchFacetValue
            class="value is-size-7-touch"
            :facet="facet"
            :def="def"
            :value="value"
          />
          <span class="count is-size-7-touch">{{ count }}</span>
        </BaseCheckbox>
        <div
          :title="lss('検索結果から除外します', 'Exclude from search result')"
          @click="exclude(value)"
          class="not-facet is-size-7-touch"
        >
          <BaseIcon
            v-if="!isSelected(value)"
            arialabel="閉じる"
            name="mdi/close-circle-outline"
            size="size-4"
          />
        </div>
      </div>
    </div>
    <div
      class="search-facet-facet is-selected not-facet-selected"
      v-for="value in notFacets"
      :key="value"
      v-show="showFacet"
    >
      <div class="value is-size-7-touch" @click.prevent="exclude(value)">
        <BaseCheckbox :indeterminate="true" class="checkbox">
          <SearchFacetValue :facet="facet" :def="def" :value="value" />
        </BaseCheckbox>
      </div>
    </div>

    <div
      class="show-remain-button is-size-7"
      v-if="showFacet && showAllFacetButton"
    >
      <button class="jps-icon-button" @click="showAllFacet()">
        {{ lss('全て表示', 'Show All') }}
        <BaseIcon name="mdi/menu-right" />
      </button>
      <SearchFacetExpandModal
        ref="searchFacetExpandModal"
        :facet="facet"
        :ss="props.ss"
        :def="def"
        :is-facet-exclude="isHina"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SearchFacetExpandModal } from '#components'
import { PREFECTURES } from '~/components/search/useSearchField'

const DEFAULT_LEN = 5
const { ls, lss } = useI18n()
const { $getContentsCode } = useNuxtApp()
const dataStore = useDataStore()
const { isHina } = useServiceType()

interface Props {
  facet: ItemFacet
  facetLength?: number
  defover?: CustomSearchField
  ss: SearchStore<any>
}
const props = withDefaults(defineProps<Props>(), {
  facetLength: DEFAULT_LEN,
})

const contentsCode: string[] = $getContentsCode()

const showFacet = ref(true)

const hasFacet = computed(() => {
  return (
    (props.facet?.counts && Object.keys(props.facet.counts).length > 0) || notFacets.value.length > 0
  )
})

const def = computed(() => {
  if (props.defover) return props.defover
  else
    return props.ss.state.searchdef.searchFields
      .filter(sf => sf.key === props.facet.key)
      .pop()
})

const sortedFacet = computed(() => {
  const obj = new Map()
  let keys = []
  if (def.value.type === 'code') {

    if (def.value.codeKey === 'jps-language') {
      // 言語の場合は検索数が多い順、かつ、不明言語(und)は最後になるように並び替える
      keys =
        code.value?.values
          ?.map(c => c.code)
          ?.filter(code => props.facet.counts[code])
          ?.sort((a, b) => props.facet.counts[b] - props.facet.counts[a]) || []
      const undIndex = keys.indexOf('und')
      if (undIndex >= 0) {
        keys.splice(undIndex, 1)
        keys.push('und')
      }
      keys = keys.slice(0, props.facetLength) || []
    } else {
      keys =
        code.value?.values
          ?.map(c => c.code)
          ?.filter(code => props.facet.counts[code])
          ?.slice(0, props.facetLength) || []
    }
  } else if (def.value.type === 'contents') {
    keys = contentsCode.slice(0, props.facetLength)
  } else if (def.value.type === 'prefectures') {
    keys = PREFECTURES
      ?.map(prefecture => prefecture.key)
      ?.filter(key => props.facet.counts[key])
      ?.slice(0, props.facetLength) || []
  } else {
    keys = Object.keys(props.facet.counts).filter(
      k => k !== 'various' && !isEmptyString(k),
    )

    if (def.value.type === 'temporal_group') {
      keys = keys
        .sort((a, b) => Number(b) - Number(a))
        .slice(0, props.facetLength)
    } else {
      keys = keys
        .sort((a, b) => props.facet.counts[b] - props.facet.counts[a])
        .slice(0, props.facetLength)
    }
  }

  keys.forEach(key => {
    obj.set(key, props.facet.counts[key])
  })
  return obj
})

const code = computed(() => {
  if (isCode.value) return dataStore.code(def.value.codeKey)
  else return null
})

const isCode = computed(() => {
  return def.value.type === 'code'
})

const select = (value: any) => {
  if (isSelected(value)) {
    props.ss.removeFilter(def.value.key, [value, '+' + value])
  } else {
    props.ss.addFilter(def.value.key, [value])
  }
}

const exclude = (value: any) => {
  select('-' + value)
}

const notFacets = computed(() => {
  if (props.ss.state.filter && props.ss.state.filter[props.facet.key]) {
    return props.ss.state.filter[def.value.key]
      .filter(value => value.startsWith('-'))
      .map(value => value.substring(1))
  }
  return []
})

const showAllFacetButton = computed(() => {
  return Object.keys(props.facet.counts).length > props.facetLength
})

const searchFacetExpandModal = ref<InstanceType<typeof SearchFacetExpandModal>>()
const showAllFacet = async () => {
  try {
    let selected = await searchFacetExpandModal.value.openAwait()
    props.ss.setFilter(def.value.key, selected)
  } catch (cancel) {}
}

function isSelected(value: any): boolean {
  if (props.ss.state.filter && props.ss.state.filter[def.value.key]) {
    return (
      props.ss.state.filter[def.value.key].includes(value) ||
      props.ss.state.filter[def.value.key].includes('+' + value)
    )
  }

  return false
}
</script>

<style lang="scss">
/* // scopedにすると、buefyのコンポーネントである.control-label以下のスタイルが崩れるため、non-scoped */
@use '@/assets/styles/scss/vue' as *;

.search-facet {
  background-color: $jps-gray-100;
  margin: 1.5rem 0;
  border-radius: 6px;
  &:first-child {
    margin-top: 0;
  }

  @at-root .hina & {
    background-color: $white;
    box-shadow: 0 0 8px 1px rgba(0, 0, 0, 0.08), 0 1px 5px 0 rgba(0, 0, 0, 0.08);
  }

  .facet-title {
    padding: 1rem 0.75rem 1rem 1rem;
    margin-bottom: 0;
    overflow: hidden;
    display: flex;
    justify-content: space-between;
    h3 {
      margin-top: 0;

      @at-root .hina & {
        font-weight: normal;
      }
    }
    .facet-toggle {
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 9999px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition-property: all;
      transition-timing-function: cubic-bezier(0.64, 0.09, 0.08, 1);
      transition-duration: 250ms;
      &:hover {
        background-color: $jps-gray-300;
      }
    }
  }

  .search-facet-facets {
    .search-facet-facet {
      padding: 0 0.75rem;
      width: 100%;
      display: flex;
      align-items: center;

      .jps-checkbox {
        margin-left: 0;
        width: 100%;
        .check {
          width: 1rem;
          height: 1rem;
        }
        .jps-checkbox-label {
          display: flex;
          align-items: center;
          line-height: 2.5rem;
          padding-left: 0.5rem;
          width: 100%;
          .value {
            flex-basis: 70%;
            line-height: 1.25rem;
            padding: 0.5rem 0rem;
            display: flex;
            align-items: center;
            i {
              margin: 0;
            }
            i + span {
              padding-left: 0.25rem;
            }
            span {
              /* @include typo-list(); */
            }
          }
          .count {
            flex-basis: 30%;
            text-align: center;
            a {
              float: right;
            }
          }
        }
      }

      &:not(:last-child) {
        border-bottom-width: 1px;
        border-collapse: separate;
        border-bottom: $jps-gray-300 1px solid;
      }
      &.not-facet-selected {
        background-color: $jps-gray-100;
        color: $jps-gray-600;
        span {
          opacity: 0.65;
        }
        a {
          color: $jps-gray-600;
        }
      }

      .not-facet {
        display: flex;
        align-items: center;
        color: $jps-gray-600;
        margin-right: 6px;
        &:hover {
          color: $jps-gray-800;
        }
        svg {
          cursor: pointer;
        }
      }
    }
  }

  .show-remain-button {
    padding: 0 0.75rem 0.75rem 0.75rem;
    display: flex;
    justify-content: flex-end;
    button {
      font-size: $size-7;  // button内にはis-size7が効かないので追加
      display: flex;
      align-items: center;
      i {
        color: $jps-gray-800;
      }
    }
  }

  .not-facet-selected {
    > div {
      padding: 0 0.75rem;
      .checkbox {
        .check {
          width: 1rem;
          height: 1rem;
        }
      }
    }
    /*&:nth-child(3) {
        margin-top: 1rem;
      }*/
    &:not(:last-child) {
      border-bottom-width: 1px;
      border-collapse: separate;
    }
    span.jps-checkbox-label {
      padding: 0.5rem;
    }
  }
}
</style>
